import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CheckCircle, Clock, Calendar, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ComplianceRecord {
  mentor_id: string;
  mentee_id: string;
  mentor_name: string;
  mentee_name: string;
  required_meetings: number;
  completed_meetings: number;
  scheduled_meetings: number;
  last_meeting_date?: string;
  next_meeting_date?: string;
  compliance_percentage: number;
  status: 'compliant' | 'at_risk' | 'non_compliant';
  academic_year: string;
}

export function MentorComplianceTracker() {
  const [complianceData, setComplianceData] = useState<ComplianceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());

  useEffect(() => {
    loadComplianceData();
  }, [selectedYear, selectedMonth]);

  const loadComplianceData = async () => {
    try {
      // Get active mentor assignments for the selected year
      const { data: assignments, error: assignmentsError } = await supabase
        .from('mentor_assignments')
        .select('mentor_id, mentee_id, academic_year')
        .eq('academic_year', selectedYear)
        .eq('status', 'active');

      if (assignmentsError) throw assignmentsError;

      if (!assignments || assignments.length === 0) {
        setComplianceData([]);
        return;
      }

      // Calculate date range for the selected month
      const startDate = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, 1);
      const endDate = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0);
      endDate.setHours(23, 59, 59, 999);

      // Get meetings for each assignment in the selected month
      const complianceRecords = await Promise.all(
        assignments.map(async (assignment) => {
          // Get mentor and mentee names
          const { data: mentorProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', assignment.mentor_id)
            .single();

          const { data: menteeProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', assignment.mentee_id)
            .single();
          const { data: meetings, error: meetingsError } = await supabase
            .from('meetings')
            .select('*')
            .eq('observer_id', assignment.mentor_id)
            .eq('observee_id', assignment.mentee_id)
            .eq('meeting_type', 'mentoring')
            .gte('scheduled_date', startDate.toISOString())
            .lte('scheduled_date', endDate.toISOString())
            .order('scheduled_date', { ascending: false });

          if (meetingsError) {
            console.error('Error loading meetings:', meetingsError);
            return null;
          }

          const completedMeetings = meetings?.filter(m => m.status === 'completed').length || 0;
          const scheduledMeetings = meetings?.filter(m => m.status === 'scheduled').length || 0;
          const requiredMeetings = 2; // Assume 2 meetings per month minimum

          const lastMeeting = meetings?.find(m => m.status === 'completed');
          const nextMeeting = meetings?.find(m => m.status === 'scheduled');

          const compliancePercentage = Math.round((completedMeetings / requiredMeetings) * 100);
          
          let status: 'compliant' | 'at_risk' | 'non_compliant';
          if (compliancePercentage >= 100) {
            status = 'compliant';
          } else if (compliancePercentage >= 50) {
            status = 'at_risk';
          } else {
            status = 'non_compliant';
          }

          return {
            mentor_id: assignment.mentor_id,
            mentee_id: assignment.mentee_id,
            mentor_name: mentorProfile ? `${mentorProfile.first_name} ${mentorProfile.last_name}` : 'Unknown Mentor',
            mentee_name: menteeProfile ? `${menteeProfile.first_name} ${menteeProfile.last_name}` : 'Unknown Mentee',
            required_meetings: requiredMeetings,
            completed_meetings: completedMeetings,
            scheduled_meetings: scheduledMeetings,
            last_meeting_date: lastMeeting?.scheduled_date,
            next_meeting_date: nextMeeting?.scheduled_date,
            compliance_percentage: Math.min(compliancePercentage, 100),
            status,
            academic_year: assignment.academic_year
          } as ComplianceRecord;
        })
      );

      setComplianceData(complianceRecords.filter(record => record !== null));
    } catch (error) {
      console.error('Error loading compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const generateComplianceReport = async () => {
    try {
      // Create a simple CSV report
      const headers = [
        'Mentor Name',
        'New Teacher Name',
        'Required Meetings',
        'Completed Meetings',
        'Scheduled Meetings',
        'Compliance %',
        'Status',
        'Last Meeting Date',
        'Next Meeting Date'
      ];

      const csvContent = [
        headers.join(','),
        ...complianceData.map(record => [
          record.mentor_name,
          record.mentee_name,
          record.required_meetings,
          record.completed_meetings,
          record.scheduled_meetings,
          record.compliance_percentage,
          record.status,
          record.last_meeting_date ? new Date(record.last_meeting_date).toLocaleDateString() : '',
          record.next_meeting_date ? new Date(record.next_meeting_date).toLocaleDateString() : ''
        ].join(','))
      ].join('\n');

      // Download the CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `mentor-compliance-report-${selectedYear}-${selectedMonth}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Compliance report downloaded successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate compliance report');
    }
  };

  const getStatusIcon = (status: ComplianceRecord['status']) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'at_risk':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'non_compliant':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusBadge = (status: ComplianceRecord['status']) => {
    const variants = {
      compliant: 'default' as const,
      at_risk: 'secondary' as const,
      non_compliant: 'destructive' as const
    };

    const labels = {
      compliant: 'Compliant',
      at_risk: 'At Risk',
      non_compliant: 'Non-Compliant'
    };

    return (
      <Badge variant={variants[status]}>
        {getStatusIcon(status)}
        <span className="ml-1">{labels[status]}</span>
      </Badge>
    );
  };

  const overallCompliance = complianceData.length > 0 
    ? Math.round(
        complianceData.reduce((sum, record) => sum + record.compliance_percentage, 0) / complianceData.length
      )
    : 0;

  const compliantCount = complianceData.filter(r => r.status === 'compliant').length;
  const atRiskCount = complianceData.filter(r => r.status === 'at_risk').length;
  const nonCompliantCount = complianceData.filter(r => r.status === 'non_compliant').length;

  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = [
    (new Date().getFullYear() - 1).toString(),
    new Date().getFullYear().toString(),
    (new Date().getFullYear() + 1).toString()
  ];

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading compliance data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overall Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mb-2">{overallCompliance}%</div>
            <Progress value={overallCompliance} className="h-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Compliant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{compliantCount}</div>
            <p className="text-xs text-muted-foreground">Meeting requirements</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">At Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{atRiskCount}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Non-Compliant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{nonCompliantCount}</div>
            <p className="text-xs text-muted-foreground">Urgent action needed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Report Generation */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Compliance Tracking</CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={generateComplianceReport} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mentor</TableHead>
                <TableHead>New Teacher</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Meetings</TableHead>
                <TableHead>Last Meeting</TableHead>
                <TableHead>Next Meeting</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {complianceData.map((record, index) => (
                <TableRow key={`${record.mentor_id}-${record.mentee_id}-${index}`}>
                  <TableCell className="font-medium">{record.mentor_name}</TableCell>
                  <TableCell>{record.mentee_name}</TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{record.compliance_percentage}%</span>
                        <span className="text-muted-foreground">
                          {record.completed_meetings}/{record.required_meetings}
                        </span>
                      </div>
                      <Progress value={record.compliance_percentage} className="h-2" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>Completed: {record.completed_meetings}</div>
                      <div>Scheduled: {record.scheduled_meetings}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {record.last_meeting_date ? (
                      <span className="text-sm">
                        {new Date(record.last_meeting_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {record.next_meeting_date ? (
                      <span className="text-sm">
                        {new Date(record.next_meeting_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(record.status)}
                  </TableCell>
                </TableRow>
              ))}
              {complianceData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No compliance data found for the selected period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}